import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMessages, useSendMessage } from '@/hooks/useMessages';
import { queryKeys } from '@/lib/queryKeys';
import { useAssignConversation } from '@/hooks/useAssignConversation';
import { useRealtimeMessages } from '@/hooks/useRealtimeMessages';
import { usePollingFallback } from '@/hooks/usePollingFallback';
import { useAttachmentUpload } from '@/hooks/useAttachmentUpload';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAuth } from '@/hooks/useAuth';
import { MessageTimeline } from '@/features/conversation/components/MessageTimeline';
import { ReplyBox } from '@/features/conversation/components/ReplyBox';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import type { ConversationStatus } from '@/api/types';

const statusConfig: Record<ConversationStatus, { label: string; color: string; bg: string }> = {
  open: { label: 'Open', color: 'text-green-700', bg: 'bg-green-100' },
  pending: { label: 'Pending', color: 'text-amber-700', bg: 'bg-amber-100' },
  resolved: { label: 'Resolved', color: 'text-blue-700', bg: 'bg-blue-100' },
  closed: { label: 'Closed', color: 'text-gray-600', bg: 'bg-gray-100' },
};

export function ConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { operatorMetadata } = useAuth();
  const queryClient = useQueryClient();
  const { isOnline } = useOnlineStatus();

  // Pending file for attachment upload
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useMessages(conversationId || '');

  const sendMessageMutation = useSendMessage(conversationId || '');
  const assignMutation = useAssignConversation(conversationId || '');

  // Realtime subscription (with tenant verification)
  const { isConnected: realtimeConnected } = useRealtimeMessages(
    conversationId || '',
    operatorMetadata?.tenant_id ?? ''
  );

  // Polling fallback (enabled when realtime is not connected AND online)
  usePollingFallback(conversationId || '', !realtimeConnected && isOnline);

  // Attachment upload
  const attachmentUpload = useAttachmentUpload(conversationId || '');

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    setPendingFile(file);
  }, []);

  // Handle send with optional attachment
  const handleSend = useCallback(
    async (text: string, idempotencyKey: string) => {
      try {
        // Send message first
        const result = await sendMessageMutation.mutateAsync({
          text,
          idempotency_key: idempotencyKey,
        });

        // If there's a pending file, upload it with the message_id
        if (pendingFile && result.message_id) {
          try {
            await attachmentUpload.upload(pendingFile, result.message_id);
            setPendingFile(null);
            // Refetch messages to get updated message with attachment
            queryClient.invalidateQueries({
              queryKey: queryKeys.messages.conversation(conversationId || ''),
            });
          } catch {
            // Upload failed - user can retry via the UI
            // Don't clear pendingFile so they can see it
          }
        }
      } catch {
        // Message send failed - optimistic UI will show "Failed - Retry"
      }
    },
    [sendMessageMutation, pendingFile, attachmentUpload, queryClient, conversationId]
  );

  // Handle cancel pending file
  const handleCancelUpload = useCallback(() => {
    setPendingFile(null);
    attachmentUpload.reset();
  }, [attachmentUpload]);

  // Handle message retry
  const handleRetryMessage = useCallback(
    (idempotencyKey: string, text: string) => {
      sendMessageMutation.mutate({ text, idempotency_key: idempotencyKey });
    },
    [sendMessageMutation]
  );

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Invalid conversation
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-red-600">Failed to load messages</p>
        <p className="text-sm text-gray-500">{error.message}</p>
        <Link to="/inbox" className="text-blue-600 hover:underline text-sm">
          Back to Inbox
        </Link>
      </div>
    );
  }

  const messages = data?.messages || [];
  const conversation = data?.conversation;
  const status = conversation?.status || 'pending';
  const statusInfo = statusConfig[status];

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Toolbar Header */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
        {/* Left section */}
        <div className="flex items-center gap-3">
          <Link
            to="/inbox"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              #{conversationId.slice(0, 8)}
            </span>

            {/* Status badge */}
            <span className={cn('px-2 py-0.5 text-xs font-medium rounded', statusInfo.bg, statusInfo.color)}>
              {statusInfo.label}
            </span>

            {/* Connection indicator */}
            <span
              className={cn(
                'w-2 h-2 rounded-full',
                !isOnline ? 'bg-red-500' : realtimeConnected ? 'bg-green-500' : 'bg-amber-500'
              )}
              title={!isOnline ? 'Offline' : realtimeConnected ? 'Connected' : 'Polling'}
            />
          </div>

          {isLoading && <Spinner size="sm" />}
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Assignment indicator */}
          {conversation?.assigned_operator_id ? (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <span className="text-[10px] text-white font-medium">O</span>
              </div>
              <span>Assigned to you</span>
            </div>
          ) : (
            <button
              onClick={() => assignMutation.mutate(operatorMetadata?.operator_id ?? null)}
              disabled={assignMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assignMutation.isPending ? <Spinner size="sm" /> : 'Assign to me'}
            </button>
          )}

          {/* More actions dropdown placeholder */}
          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Message Timeline */}
      <MessageTimeline
        messages={messages}
        isLoading={isLoading}
        hasMore={hasNextPage || false}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onRetryMessage={handleRetryMessage}
      />

      {/* Reply Box */}
      <ReplyBox
        onSend={handleSend}
        isSending={sendMessageMutation.isPending}
        disabled={conversation?.status === 'closed' || !isOnline}
        // Attachment props
        onFileSelect={handleFileSelect}
        pendingFile={pendingFile}
        onCancelUpload={handleCancelUpload}
        uploadState={attachmentUpload.state}
        uploadProgress={attachmentUpload.progress}
        uploadError={attachmentUpload.error}
        onRetryUpload={attachmentUpload.retry}
      />
    </div>
  );
}
